import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Upload, Download, Send, Search, PhoneCall, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, StatusBadge } from "@/components/Brand";
import ContactFormDialog from "@/components/ContactFormDialog";
import ImportWizard from "@/components/ImportWizard";
import ContactDrawer from "@/components/ContactDrawer";
import { api, errMsg, useData } from "@/lib/api";
import { downloadCsv, fullName, STATUS_LABELS, TEMPERATURES, TEMP_STYLE } from "@/lib/crm";
import { useAuth } from "@/context/AuthContext";
import { useStickyState } from "@/lib/useStickyState";
import WorkspaceFilter from "@/components/WorkspaceFilter";

const UNASSIGNED = "__none__";

export default function Contacts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  const [contacts, reload, loading] = useData("/contacts", []);
  const [associates] = useData(isAdmin ? "/admin/students" : null, []);
  const [q, setQ] = useState("");
  const [workspace, setWorkspace] = useStickyState("contacts.workspace", "all");
  const [tempFilter, setTempFilter] = useStickyState("contacts.tempFilter", "all");
  const [labelFilter, setLabelFilter] = useStickyState("contacts.labelFilter", "all");
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ open: false, contact: null });
  const [importOpen, setImportOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);

  const allLabels = [...new Set(contacts.flatMap((c) => c.tags || []))].sort();
  const scoped = workspace === "all" ? contacts : contacts.filter((c) => c.owner_id === workspace || c.assigned_to === workspace);
  const filtered = scoped
    .filter((c) => tempFilter === "all" || c.temperature === tempFilter)
    .filter((c) => labelFilter === "all" || (c.tags || []).includes(labelFilter))
    .filter((c) => `${fullName(c)} ${c.email} ${c.phone} ${(c.tags || []).join(" ")} ${c.status} ${c.assigned_to_name || ""}`.toLowerCase().includes(q.toLowerCase()));
  const allSelected = filtered.length > 0 && filtered.every((c) => selected.includes(c.id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const doExport = async () => {
    const rows = selected.length ? contacts.filter((c) => selected.includes(c.id)) : filtered;
    if (!rows.length) return toast.error("No leads to export.");
    const untagged = rows.filter((c) => !c.temperature);
    if (untagged.length) return toast.error(`Label all leads first — ${untagged.length} lead(s) missing a Hot/Warm/Cold tag.`);
    downloadCsv(rows);
    await api.post("/contacts/export", { count: rows.length });
    toast.success(`${rows.length} records exported to CSV`);
  };

  const createCampaign = () => {
    if (!selected.length) return toast.error("Select at least one contact first");
    navigate("/campaigns", { state: { recipientIds: selected } });
  };

  const assign = async (id, value) => {
    const assigned_to = value === UNASSIGNED ? null : value;
    try {
      await api.patch(`/contacts/${id}/assign`, { assigned_to });
      toast.success(assigned_to ? "Lead assigned" : "Lead unassigned");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const bulkAssign = async (value) => {
    const assigned_to = value === UNASSIGNED ? null : value;
    try {
      await Promise.all(selected.map((id) => api.patch(`/contacts/${id}/assign`, { assigned_to })));
      toast.success(`${selected.length} lead(s) ${assigned_to ? "assigned" : "unassigned"}`);
      setSelected([]);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Contacts & Leads" subtitle="Your prospects and clients. Click a lead to view details, history, tasks and files.">
        <Button onClick={() => setForm({ open: true, contact: null })} className="bg-[#3B185F] hover:bg-[#250E3F]" data-testid="add-contact-btn"><Plus className="h-4 w-4 mr-1.5" />Add Lead</Button>
        <Button variant="outline" onClick={() => setImportOpen(true)} className="border-[#3B185F]/25" data-testid="import-btn"><Upload className="h-4 w-4 mr-1.5" />Import</Button>
        <Button variant="outline" onClick={doExport} className="border-[#3B185F]/25" data-testid="export-btn"><Download className="h-4 w-4 mr-1.5" />Export{selected.length ? ` (${selected.length})` : ""}</Button>
        <Button onClick={createCampaign} className="bg-[#D4AF37] text-[#19082B] hover:bg-[#C59B27]" data-testid="create-campaign-from-contacts-btn"><Send className="h-4 w-4 mr-1.5" />Email{selected.length ? ` (${selected.length})` : ""}</Button>
      </PageHeader>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, tag, stage, associate" className="pl-9" data-testid="contacts-search-input" />
        </div>
        <div className="flex items-center gap-3">
          <Select value={tempFilter} onValueChange={setTempFilter}>
            <SelectTrigger className="w-40" data-testid="temp-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="temp-filter-all">All temperatures</SelectItem>
              {TEMPERATURES.map((t) => <SelectItem key={t} value={t} data-testid={`temp-filter-${t}`}>{t} leads</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={labelFilter} onValueChange={setLabelFilter}>
            <SelectTrigger className="w-40" data-testid="label-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="label-filter-all">All labels</SelectItem>
              {allLabels.map((l) => <SelectItem key={l} value={l} data-testid={`label-filter-${l}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <WorkspaceFilter associates={associates} value={workspace} onChange={setWorkspace} />
          {isAdmin && selected.length > 0 && (
            <Select onValueChange={bulkAssign}>
              <SelectTrigger className="w-56" data-testid="bulk-assign-select"><SelectValue placeholder={`Assign ${selected.length} to…`} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassign</SelectItem>
                {associates.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`bulk-assign-option-${a.id}`}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="text-sm text-stone-500" data-testid="contacts-count">{filtered.length} contacts · {selected.length} selected</div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border bg-white">
        <table className="w-full text-sm" data-testid="contacts-table">
          <thead className="bg-[#F6F2FB] text-left text-xs uppercase tracking-wider text-[#3B185F]">
            <tr>
              <th className="p-3 w-10"><Checkbox checked={allSelected} onCheckedChange={(v) => setSelected(v ? filtered.map((c) => c.id) : [])} data-testid="select-all-contacts" /></th>
              <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Tags</th><th className="p-3">Stage</th>
              <th className="p-3">Assigned To</th>
              <th className="p-3 text-right">Quick</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} onClick={() => setDrawerId(c.id)} className="border-t cursor-pointer transition-colors hover:bg-[#FFFDF0]" data-testid={`contact-row-${c.id}`}>
                <td className="p-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`select-contact-${c.id}`} /></td>
                <td className="p-3 font-semibold text-[#19082B]"><div className="flex items-center gap-2">{fullName(c)}{c.temperature ? <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${TEMP_STYLE[c.temperature]}`} data-testid={`contact-temp-${c.id}`}>{c.temperature}</span> : <span className="rounded-full border border-dashed border-stone-300 px-2 py-0.5 text-[10px] text-stone-400" data-testid={`contact-needstag-${c.id}`}>Needs tag</span>}</div></td>
                <td className="p-3 text-stone-600">{c.email}</td>
                <td className="p-3 text-stone-600 whitespace-nowrap">{c.phone}</td>
                <td className="p-3"><div className="flex flex-wrap gap-1">{(c.tags || []).map((t) => <span key={t} className="rounded-full bg-[#F6F2FB] px-2 py-0.5 text-xs text-[#3B185F]">{t}</span>)}</div></td>
                <td className="p-3"><StatusBadge status={c.status} label={STATUS_LABELS[c.status]} /></td>
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  {isAdmin ? (
                    <Select value={c.assigned_to || UNASSIGNED} onValueChange={(v) => assign(c.id, v)}>
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid={`contact-assign-${c.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                        {associates.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`contact-assign-${c.id}-option-${a.id}`}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-xs text-stone-500">{c.assigned_to_name || "—"}</span>
                  )}
                </td>
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <button type="button" onClick={() => navigate("/dialer", { state: { contactId: c.id } })} className="rounded-md border p-1.5 text-[#3B185F] hover:bg-[#F6F2FB]" title="Dial" data-testid={`contact-dial-${c.id}`}><PhoneCall className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => navigate("/sms", { state: { contactId: c.id } })} className="rounded-md border p-1.5 text-[#3B185F] hover:bg-[#F6F2FB]" title="SMS" data-testid={`contact-sms-${c.id}`}><MessageSquare className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && !filtered.length && <tr><td colSpan={8} className="p-10 text-center text-stone-500">No contacts found.</td></tr>}
          </tbody>
        </table>
      </div>

      <ContactFormDialog open={form.open} contact={form.contact} onOpenChange={(v) => setForm((f) => ({ ...f, open: v }))} onSaved={reload} />
      <ImportWizard open={importOpen} onOpenChange={setImportOpen} onImported={reload} />
      <ContactDrawer
        contact={contacts.find((c) => c.id === drawerId)}
        onClose={() => setDrawerId(null)}
        onEdit={(c) => setForm({ open: true, contact: c })}
        onDelete={async (c) => {
          try { await api.delete(`/contacts/${c.id}`); toast.success(`${fullName(c)} deleted`); setDrawerId(null); reload(); }
          catch (e) { toast.error(errMsg(e)); }
        }}
      />
    </div>
  );
}
